/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import {
  ArrowRight,
  ShieldCheck,
  Zap,
  Lock,
} from "lucide-react";

interface LandingHeroProps {
  onGetStarted: () => void;
  onExploreServices: () => void;
  onLogin?: () => void;
}

export const LandingHero: React.FC<LandingHeroProps> = ({
  onGetStarted,
  onExploreServices,
  onLogin,
}) => {
  return (
    <section id="hero-section" className="relative overflow-hidden bg-white text-[#111827] py-6 sm:py-10 lg:py-14 border-b border-[#E5E7EB]">
      
      {/* Background Subtle Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(15,45,92,0.04)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />

      <div className="max-w-4xl mx-auto px-3.5 sm:px-6 lg:px-8 relative z-10 text-center w-full">
        <div className="w-full flex flex-col gap-3.5 sm:gap-5">
          
          {/* Main Semantic H1 Headline */}
          <h1
            id="hero-main-heading"
            className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight leading-[1.25] text-[#111827]"
          >
            Instant Identity Verification, Business Filing &amp;{" "}
            <span className="text-[#0F2D5C]">
              Automated Utility Bills in Nigeria
            </span>
          </h1>

          {/* High-Intent Subheading */}
          <p
            id="hero-subheading"
            className="text-xs sm:text-sm md:text-base text-[#374151] font-normal leading-relaxed max-w-2xl mx-auto"
          >
            Verify NIN and BVN records with slip downloads, register businesses with CAC, and settle electricity, data, and airtime transactions in seconds through one unified portal and developer API.
          </p>

          {/* 3 Focused Benefit Cards */}
          <div
            id="hero-benefits-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 pt-0.5 text-left max-w-3xl mx-auto w-full"
          >
            <div className="flex items-start gap-2 p-2.5 sm:p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] shadow-2xs">
              <ShieldCheck className="h-4 w-4 text-[#0F2D5C] shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <h2 className="text-xs sm:text-sm font-bold text-[#0F2D5C]">Instant Verification &amp; Slips</h2>
                <p className="text-[10.5px] sm:text-[11px] text-[#374151] mt-0.5 leading-snug">Regular/premium NIN slips, BVN cards, and CAC reports with QR codes.</p>
              </div>
            </div>

            <div className="flex items-start gap-2 p-2.5 sm:p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] shadow-2xs">
              <Zap className="h-4 w-4 text-[#0F2D5C] shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <h2 className="text-xs sm:text-sm font-bold text-[#0F2D5C]">Automated Bills &amp; VTU</h2>
                <p className="text-[10.5px] sm:text-[11px] text-[#374151] mt-0.5 leading-snug">Zero-delay Disco tokens, cheap SME data bundles, airtime, and exam pins.</p>
              </div>
            </div>

            <div className="flex items-start gap-2 p-2.5 sm:p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] shadow-2xs">
              <Lock className="h-4 w-4 text-[#0F2D5C] shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <h2 className="text-xs sm:text-sm font-bold text-[#0F2D5C]">Bank-Grade &amp; 99.9% Uptime</h2>
                <p className="text-[10.5px] sm:text-[11px] text-[#374151] mt-0.5 leading-snug">Dedicated virtual account funding, encrypted payouts, and REST APIs.</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            id="hero-actions"
            className="flex flex-col items-center justify-center gap-2.5 pt-2"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto">
              <button
                id="hero-primary-btn"
                onClick={onLogin || onGetStarted}
                type="button"
                data-tool-name="login"
                data-tool-description="Authenticate user or agent to access client portal"
                data-agent-action="login"
                data-agent-target="auth-portal"
                className="w-full sm:w-auto min-h-[48px] px-8 py-3.5 bg-[#0F2D5C] hover:bg-[#17407E] active:bg-[#0A1E3F] text-white font-bold rounded-xl text-xs sm:text-sm shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 touch-manipulation focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F2D5C]"
              >
                <span>Log in</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                id="hero-secondary-btn"
                onClick={onExploreServices}
                type="button"
                data-tool-name="explore_services"
                data-tool-description="Browse catalog of 25+ identity verification, CAC registration, and utility services"
                data-agent-action="explore_services"
                data-agent-target="services-catalog"
                className="w-full sm:w-auto min-h-[48px] px-8 py-3.5 bg-white hover:bg-[#F5F7FA] active:bg-[#E5E7EB] text-[#0F2D5C] border border-[#0F2D5C] font-bold rounded-xl text-xs sm:text-sm shadow-xs transition-all cursor-pointer flex items-center justify-center gap-2 touch-manipulation focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F2D5C]"
              >
                <span>Explore Services &amp; Pricing</span>
              </button>
            </div>
            <p className="text-[11px] sm:text-xs text-[#374151] pt-1.5 flex items-center justify-center gap-1.5 flex-wrap">
              <span>Instant Automated Processing</span>
              <span aria-hidden="true">•</span>
              <span>Dedicated Virtual Account Funding</span>
              <span aria-hidden="true">•</span>
              <span>No Hidden Setup Fees</span>
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default LandingHero;
